// @greenlight-spec: 28-it-dashboard-ui.md
// @greenlight-spec: 27-fleet-health-monitor.md
// @greenlight-spec: 19-design-system.md
import * as React from 'react';

import { MetricSparkline } from '@/components/greenlight/MetricSparkline';
import { MonoValue } from '@/components/greenlight/MonoValue';
import { StatusOrb } from '@/components/greenlight/StatusOrb';
import { cn } from '@/lib/utils';
import {
  APP_CAPACITY_CRITICAL_UTILIZATION,
  APP_CAPACITY_WARN_UTILIZATION,
  assessAppRuntimeCapacity,
  assessControlPlaneMemory,
  clusterCapacityPeak,
  clusterCapacityTotals,
  QUEUE_BACKLOG_ALERT_SECONDS,
  type ControlPlaneReplica,
  type HealthFreshness,
  type ClusterTotals,
  type HealthSnapshot,
} from '@greenlight/shared';

import { CONTROL_PLANE_TIERS, PLATFORM_SERVICES } from './ControlPlaneServicesSection';
import { knownVersion, releaseLabel, shortCommit } from './versionLabels';

/**
 * What the hero reads: the snapshot minus the parts only the cards below need,
 * plus the whole-cluster totals a fleet report carries in place of the nodes.
 */
export type HealthHeroSnapshot = Pick<
  HealthSnapshot,
  | 'pods'
  | 'platform_services'
  | 'provider_resource_health'
  | 'queue'
  | 'app_runtime_capacity'
  | 'cluster_nodes'
  | 'versions'
  | 'trends'
> & { cluster_totals?: ClusterTotals };

const MIB = 1024 * 1024;

// Fallback control-plane memory limit, used only where no replica reported the
// limit it is actually running under (off-cluster, or a pod read that failed).
// The chart default in docs/22; a customer override is read from the snapshot.
const MEM_LIMIT_FALLBACK_MIB = 1024;

/**
 * The replica nearest its own limit for one resource, with that replica's own
 * limit beside its usage. Each resource picks its own replica: the one nearest
 * its memory limit is not necessarily the one nearest its CPU limit, and one
 * replica's usage over another's limit is not a percentage of anything.
 */
function highestReplica(
  replicas: ControlPlaneReplica[] | undefined,
  resource: 'memory' | 'cpu',
): { used: number; limit: number } | null {
  let best: { used: number; limit: number } | null = null;
  for (const r of replicas ?? []) {
    const reading =
      resource === 'memory'
        ? { used: r.memory.usage_bytes, limit: r.memory.limit_bytes }
        : { used: r.cpu.usage_cores, limit: r.cpu.limit_cores };
    if (reading.used === null || reading.limit === null || reading.limit <= 0) continue;
    const candidate = { used: reading.used, limit: reading.limit };
    if (best === null || candidate.used / candidate.limit > best.used / best.limit)
      best = candidate;
  }
  return best;
}

// Control-plane CPU and memory carry the same warn/critical bounds the verdict
// judges memory on (`assessControlPlaneMemory`), so the badge, the meter and the
// headline can never disagree about one reading.
const CPU_BANDS = {
  max: 100,
  warn: APP_CAPACITY_WARN_UTILIZATION,
  crit: APP_CAPACITY_CRITICAL_UTILIZATION,
};
function memBands(limitMb: number): { max: number; warn: number; crit: number } {
  return {
    max: limitMb,
    warn: Math.round((limitMb * APP_CAPACITY_WARN_UTILIZATION) / 100),
    crit: Math.round((limitMb * APP_CAPACITY_CRITICAL_UTILIZATION) / 100),
  };
}

const numberFmt = new Intl.NumberFormat('en-US');

/** One service as the verdict judges it, named the way the services card names it. */
interface JudgedService {
  label: string;
  downTone: 'red' | 'amber';
  possessive: 'its' | 'their';
  ready_replicas: number;
  desired_replicas: number;
  replicas: readonly Pick<ControlPlaneReplica, 'replica_id' | 'memory'>[];
  unreadable: boolean;
  /** A snapshot from before the per-Deployment read: single-replica, not a named service. */
  legacy?: true;
}

/**
 * The primary server, the async worker and every platform service the snapshot
 * read, in the services card's order. A snapshot from before the per-Deployment
 * read has no tiers, and its replicas, if any, are judged as one service.
 */
function judgedServices(s: HealthHeroSnapshot): JudgedService[] {
  const pod = s.pods.control_plane;
  const tiers = pod.tiers;
  const core: JudgedService[] = tiers
    ? CONTROL_PLANE_TIERS.map((meta) => ({
        label: meta.label,
        downTone: 'red',
        possessive: 'its',
        ...tiers[meta.key],
        replicas: (pod.replicas ?? []).filter((r) => r.tier === meta.key),
        unreadable: false,
      }))
    : [
        {
          label: 'Control plane',
          downTone: 'red',
          possessive: 'its',
          ready_replicas: pod.ready_replicas,
          desired_replicas: pod.desired_replicas,
          replicas: pod.replicas ?? [],
          unreadable: false,
          legacy: true,
        },
      ];
  const platform = PLATFORM_SERVICES.flatMap((meta): JudgedService[] => {
    const service = s.platform_services?.find((p) => p.key === meta.key);
    if (!service) return [];
    return [
      {
        label: meta.label,
        downTone: meta.downTone,
        possessive: meta.possessive,
        ready_replicas: service.ready_replicas,
        desired_replicas: service.desired_replicas,
        replicas: service.replicas,
        unreadable: service.read_error === true,
      },
    ];
  });
  return [...core, ...platform];
}

function isDown(svc: JudgedService): boolean {
  if (svc.unreadable) return false;
  // A pre-tier snapshot is single-replica, so any shortfall there is the service down.
  if (svc.legacy) return svc.ready_replicas === 0 || svc.ready_replicas < svc.desired_replicas;
  return svc.desired_replicas > 0 && svc.ready_replicas === 0;
}

const isShort = (svc: JudgedService): boolean =>
  !svc.unreadable && !isDown(svc) && svc.ready_replicas < svc.desired_replicas;

// One-line hero summary in the same vocabulary as the services card below it:
// services an org admin can name, not pods. A failed Deployment read yields no
// count at all, because the replica numbers the snapshot carries in that case
// are the collector's sentinel rather than cluster truth.
function controlPlaneSummary(s: HealthHeroSnapshot): string {
  const pod = s.pods.control_plane;
  if (pod.read_error) return 'services unreadable';
  if (!pod.tiers)
    return `${pod.ready_replicas}/${pod.desired_replicas} pods ready across all services`;
  const services = judgedServices(s);
  const running = services.filter(
    (svc) =>
      !svc.unreadable && svc.ready_replicas > 0 && svc.ready_replicas >= svc.desired_replicas,
  ).length;
  return `${running} of ${services.length} services running`;
}

// Derives the page's verdict from the snapshot, judging each service by what
// breaks when it is down and naming the one in trouble. Every red is settled
// before any amber, so an unreadable measurement never outranks a fact that was
// read. A memory warning never outranks the service being down, so a service
// whose outage is amber never turns the page red on memory alone.
export function deriveControlPlaneVerdict(
  s: HealthHeroSnapshot,
  freshness?: Pick<HealthFreshness, 'stale'>,
  live?: { credentialsRejected?: boolean },
): {
  tone: 'green' | 'amber' | 'red';
  headline: string;
} {
  // A stale row cannot answer the present-tense question, whatever it says. The
  // collector that wrote it may have died an hour ago, and its last write is
  // green by construction — nothing unhealthy gets collected after the thing
  // doing the collecting stops.
  if (freshness?.stale) {
    return { tone: 'amber', headline: 'Current health unknown — no recent observation' };
  }
  const pod = s.pods.control_plane;
  // An unreadable Deployment state is not a healthy one: the replica counts carry
  // no information, so they must not be read as a pass. When the live posture
  // read was refused for a rejected pod token, that is the likelier reason the
  // collector's read failed too — both pods share the ServiceAccount — and the
  // state clears on its own, so the headline names it.
  if (pod.read_error) {
    return {
      tone: 'red',
      headline: live?.credentialsRejected
        ? 'Cluster rejected the control plane’s credentials'
        : 'Primary server and async worker unreadable',
    };
  }
  const services = judgedServices(s);
  const memoryOf = (svc: JudgedService) => assessControlPlaneMemory(svc.replicas).level;
  const anyUnavailable = Object.values(s.provider_resource_health).some((v) => v === 'unavailable');
  // A cluster that cannot schedule a new app pod is not healthy even when every
  // service is ready and every provider is 'available'.
  const capacity = assessAppRuntimeCapacity(
    s.app_runtime_capacity,
    s.cluster_nodes ?? s.cluster_totals,
  );

  const redDown = services.find((svc) => svc.downTone === 'red' && isDown(svc));
  if (redDown) {
    return {
      tone: 'red',
      headline: redDown.legacy ? 'Service disruption' : `${redDown.label} down`,
    };
  }
  if (anyUnavailable) {
    return { tone: 'red', headline: 'Service disruption' };
  }
  // Cluster capacity and a container's own memory are different questions, and
  // the másLabor OOM is what separates them: scheduling headroom was fine while
  // the container serving /auth/check ran out of its own memory.
  const redMemory = services.find((svc) => svc.downTone === 'red' && memoryOf(svc) === 'critical');
  if (redMemory) {
    return {
      tone: 'red',
      headline: `${redMemory.label} near ${redMemory.possessive} memory limit`,
    };
  }
  if (capacity.level === 'critical') {
    return { tone: 'red', headline: 'Cluster capacity exhausted' };
  }
  // Replicas that exist but could not be measured is a broken reading, not a
  // healthy one. No replicas at all is a different fact — off-cluster there is
  // nothing to read — and stays out of this. It ranks below the red branches:
  // pod metrics and node allocatable are separate reads that fail separately,
  // and a known red must not be hidden behind an unknown.
  if (
    (pod.replicas?.length ?? 0) > 0 &&
    assessControlPlaneMemory(pod.replicas).level === 'unknown'
  ) {
    return { tone: 'amber', headline: 'Primary server and async worker not measured' };
  }

  const anyDegraded = Object.values(s.provider_resource_health).some((v) => v === 'degraded');
  // Work that stopped progressing counts as trouble even when nothing has
  // recorded an error: a job held past its duration and one that spent its last
  // attempt are both invisible to the failure counter.
  const queueIssue =
    s.queue.failed_last_1h > 0 ||
    s.queue.oldest_pending_age_s >= QUEUE_BACKLOG_ALERT_SECONDS ||
    (s.queue.locked_over_duration ?? 0) > 0 ||
    (s.queue.abandoned ?? 0) > 0 ||
    (s.queue.exhausted ?? 0) > 0;

  if (anyDegraded || queueIssue) {
    return { tone: 'amber', headline: 'Degraded performance' };
  }
  const amberDown = services.find(isDown);
  if (amberDown) {
    return { tone: 'amber', headline: `${amberDown.label} down` };
  }
  const short = services.find(isShort);
  if (short) {
    return { tone: 'amber', headline: `${short.label} at reduced capacity` };
  }
  const unreadable = services.find((svc) => svc.unreadable);
  if (unreadable) {
    return { tone: 'amber', headline: `${unreadable.label} status unreadable` };
  }
  const amberMemory = services.find((svc) => memoryOf(svc) === 'critical');
  if (amberMemory) {
    return {
      tone: 'amber',
      headline: `${amberMemory.label} near ${amberMemory.possessive} memory limit`,
    };
  }
  const memoryHigh = services.find((svc) => memoryOf(svc) === 'warning');
  if (memoryHigh) {
    return { tone: 'amber', headline: `${memoryHigh.label} memory high` };
  }
  if (capacity.level === 'warning') {
    return { tone: 'amber', headline: 'Cluster capacity low' };
  }

  return { tone: 'green', headline: 'All systems healthy' };
}

/**
 * `unknown` is a reading that was not taken, which must not wear the healthy
 * colour: an unmeasured control plane and a comfortable one look identical
 * under a green dot, and only one of them is fine.
 */
type VitalTone = 'ok' | 'warn' | 'unknown';

interface VitalProps {
  label: string;
  value: string;
  caption: string;
  tone: VitalTone;
  trend: number[];
  domainMax?: number;
  thresholds?: { warn?: number; crit?: number };
  referenceValue?: number;
}

const VITAL_DOT_CLASS: Record<VitalTone, string> = {
  ok: 'bg-[hsl(var(--status-green))]',
  warn: 'bg-[hsl(var(--status-amber))]',
  unknown: 'bg-slate/40',
};

const VITAL_VALUE_CLASS: Record<VitalTone, string> = {
  ok: 'text-[hsl(var(--status-green-text))]',
  warn: 'text-[hsl(var(--status-amber-text))]',
  unknown: 'text-muted-foreground',
};

const VITAL_TONE_WORD: Record<VitalTone, string> = {
  ok: 'ok',
  warn: 'warning',
  unknown: 'not measured',
};

function Vital({
  label,
  value,
  caption,
  tone,
  trend,
  domainMax,
  thresholds,
  referenceValue,
}: VitalProps): React.ReactElement {
  const dotClass = VITAL_DOT_CLASS[tone];
  // Value text tracks its state (AA-tuned -text tokens), matching the design.
  const valueClass = VITAL_VALUE_CLASS[tone];

  return (
    <div className="flex flex-col gap-1 bg-card px-4 py-3">
      {/* Dot sits before the label (same as App health / resource tiles), not after the value. */}
      <div className="flex items-center gap-1.5">
        <span className={cn('size-1.5 shrink-0 rounded-full', dotClass)} aria-hidden="true" />
        <span className="font-mono text-[10px] uppercase tracking-[0.08em] text-muted-foreground">
          {label}
        </span>
        <span className="sr-only">{VITAL_TONE_WORD[tone]}</span>
      </div>
      <span className={cn('text-xl font-semibold tabular-nums', valueClass)}>{value}</span>
      <p className="text-[11px] text-muted-foreground">{caption}</p>
      {thresholds !== undefined ? (
        <MetricSparkline
          data={trend}
          domainMax={domainMax}
          thresholds={thresholds}
          referenceValue={referenceValue}
        />
      ) : (
        <MetricSparkline data={trend} tone={tone === 'warn' ? 'amber' : 'primary'} />
      )}
    </div>
  );
}

// Headline text tracks the control-plane verdict (AA-tuned -text tokens).
const HEADLINE_CLASS: Record<'green' | 'amber' | 'red', string> = {
  green: 'text-[hsl(var(--status-green-text))]',
  amber: 'text-[hsl(var(--status-amber-text))]',
  red: 'text-[hsl(var(--status-red-text))]',
};

/**
 * The verdict, the facts that support it, and the three vitals: is Greenlight
 * about to run out of room, and can the cluster still place a new pod?
 *
 * `aggregateFallback` lets a snapshot with no replicas at all stand in the
 * older collector's combined control-plane CPU and memory for the primary
 * server. Those figures are the worst replica across both services, so a
 * surface that cannot say which service they describe leaves it off.
 */
export function HealthHero({
  snapshot,
  freshness,
  aggregateFallback = true,
  credentialsRejected = false,
}: {
  readonly snapshot: HealthHeroSnapshot;
  readonly freshness?: Pick<HealthFreshness, 'stale'>;
  readonly aggregateFallback?: boolean;
  /** The live updater posture's verdict that the cluster refused the pod's own token. */
  readonly credentialsRejected?: boolean;
}): React.ReactElement {
  const verdict = deriveControlPlaneVerdict(snapshot, freshness, { credentialsRejected });
  const pod = snapshot.pods.control_plane;
  const capacity = snapshot.app_runtime_capacity ?? null;

  const availableServices = Object.values(snapshot.provider_resource_health).filter(
    (v) => v === 'available',
  ).length;
  const totalServices = Object.values(snapshot.provider_resource_health).length;

  // The hero's two resource tiles are the primary server's alone; every other
  // service is judged on the services card and named by the headline.
  const serveReplicas = pod.replicas?.filter((r) => r.tier === 'serve');
  const replicaCount = serveReplicas?.length ?? 0;
  const highestOf = replicaCount > 1 ? `highest of ${replicaCount} replicas` : 'vs its limit';
  // Read from the replicas when the snapshot has them; the aggregate fields are
  // the older collector's reading, cover both tiers and have no limit of their
  // own, so they stand in only for a snapshot with no replicas at all.
  // Tiles compare the unrounded reading against their bounds, as the verdict
  // does, and display it rounded.
  const memHigh = highestReplica(serveReplicas, 'memory');
  const memUsedMib = memHigh
    ? Math.round(memHigh.used / MIB)
    : serveReplicas || !aggregateFallback
      ? null
      : (pod.memory_usage_mb ?? null);
  const memoryLimit = memHigh ? Math.round(memHigh.limit / MIB) : MEM_LIMIT_FALLBACK_MIB;
  const memBand = memBands(memoryLimit);
  const memReading = memHigh
    ? (memHigh.used / memHigh.limit) * 100
    : memUsedMib === null
      ? null
      : (memUsedMib / memoryLimit) * 100;
  const cpuHigh = highestReplica(serveReplicas, 'cpu');
  const cpuReading = cpuHigh
    ? (cpuHigh.used / cpuHigh.limit) * 100
    : serveReplicas || !aggregateFallback
      ? null
      : (pod.cpu_usage_percent ?? null);
  const clusterTotals = clusterCapacityTotals(
    snapshot.cluster_nodes ?? snapshot.cluster_totals,
    capacity,
  );
  const capacityReading = clusterTotals ? clusterCapacityPeak(clusterTotals) : null;
  const notMeasured = aggregateFallback || serveReplicas ? 'Not measured' : 'Not reported';

  // Every tile is a measurement the install actually takes — a figure no
  // collector fills belongs in a section that hides itself, not in the hero.
  const vitals: VitalProps[] = [
    {
      label: 'Primary server memory',
      value:
        memUsedMib === null
          ? notMeasured
          : `${numberFmt.format(memUsedMib)} / ${numberFmt.format(memoryLimit)} MiB`,
      caption: highestOf,
      tone:
        memReading === null
          ? 'unknown'
          : memReading >= APP_CAPACITY_WARN_UTILIZATION
            ? 'warn'
            : 'ok',
      trend: snapshot.trends?.['primary_server_memory'] ?? [],
      domainMax: memBand.max,
      thresholds: { warn: memBand.warn, crit: memBand.crit },
      referenceValue: memBand.crit,
    },
    {
      label: 'Primary server CPU',
      value: cpuReading === null ? notMeasured : `${Math.round(cpuReading)}%`,
      caption: highestOf,
      tone: cpuReading === null ? 'unknown' : cpuReading >= CPU_BANDS.warn ? 'warn' : 'ok',
      trend: snapshot.trends?.['primary_server_cpu'] ?? [],
      domainMax: CPU_BANDS.max,
      thresholds: { warn: CPU_BANDS.warn, crit: CPU_BANDS.crit },
      referenceValue: CPU_BANDS.crit,
    },
    {
      label: 'Cluster capacity used',
      value: capacityReading === null ? notMeasured : `${Math.round(capacityReading)}%`,
      caption: 'reserved by pods, of what nodes offer',
      tone:
        capacityReading === null
          ? 'unknown'
          : capacityReading >= APP_CAPACITY_WARN_UTILIZATION
            ? 'warn'
            : 'ok',
      trend: snapshot.trends?.['cluster_capacity'] ?? [],
      domainMax: 100,
      thresholds: { warn: APP_CAPACITY_WARN_UTILIZATION, crit: APP_CAPACITY_CRITICAL_UTILIZATION },
      referenceValue: APP_CAPACITY_CRITICAL_UTILIZATION,
    },
  ];

  // Install identity: one release and one commit for the whole install. Older
  // snapshots carry the commit only, under `control_plane_image`.
  const release = knownVersion(snapshot.versions.release_version);
  const sourceCommit =
    knownVersion(snapshot.versions.source_commit) ??
    knownVersion(snapshot.versions.control_plane_image);

  return (
    <div className="rounded-gl-lg border border-border bg-card p-5 md:p-6">
      <div className="flex items-center gap-3">
        <StatusOrb tone={verdict.tone} />
        <div className="min-w-0">
          <p className="font-mono text-[10px] uppercase tracking-[0.08em] text-muted-foreground">
            Control plane
          </p>
          <p
            className={cn(
              'text-2xl md:text-3xl font-extrabold tracking-tight',
              HEADLINE_CLASS[verdict.tone],
            )}
          >
            {verdict.headline}
          </p>
          <p className="text-xs text-muted-foreground mt-0.5 flex flex-wrap gap-x-2">
            <span>{controlPlaneSummary(snapshot)}</span>
            <span>·</span>
            <span>
              {availableServices} of {totalServices} cloud resources available
            </span>
            <span>·</span>
            {release !== undefined ? (
              <>
                <span>
                  release <MonoValue>{releaseLabel(release)}</MonoValue>
                </span>
                <span>·</span>
              </>
            ) : (
              // An install with no chart-injected release has one, it just did
              // not report it. Saying so beats implying it has no version.
              <>
                <span>release not reported</span>
                <span>·</span>
              </>
            )}
            {sourceCommit !== undefined && (
              <>
                <span>
                  commit <MonoValue>{shortCommit(sourceCommit)}</MonoValue>
                </span>
                <span>·</span>
              </>
            )}
            <span>
              {pod.restart_count_24h === undefined
                ? 'Restarts in 24h not reported'
                : `${pod.restart_count_24h} restarts in 24h across all services`}
            </span>
          </p>
        </div>
      </div>

      {/* Vitals strip — hairline-seam grid, matching App health / cloud resources */}
      <div className="mt-5 overflow-hidden rounded-[8px] border border-border bg-border">
        <div className="grid grid-cols-1 gap-px sm:grid-cols-3">
          {vitals.map((v) => (
            <Vital key={v.label} {...v} />
          ))}
        </div>
      </div>
    </div>
  );
}
